import React from "react";
import { Loader2 } from "lucide-react";

/**
 * Shared availability slider — reused by Product Availability (ProductDetail
 * AvailabilityBar) and Shop Availability (vendor dashboard).
 *
 * Visual contract (copied from the shipped Product Availability bar):
 *   .mt-5 rounded-xl border border-slate-200 bg-white px-3 py-3
 *   .flex items-center justify-between gap-3 flex-wrap
 *   Track h-7 w-14 rounded-full; Thumb h-6 w-6 rounded-full bg-white shadow
 *   Active color: bg-emerald-600 | Inactive: bg-slate-300
 *   Translates +28px when active
 *   Loader2 spinner shown inside the thumb while busy
 *
 * Only the labels and data-source differ between usages.
 */
export default function AvailabilityToggle({
  active,
  onToggle,
  busy = false,
  eyebrow = "Availability",
  activeTitle = "Available",
  inactiveTitle = "Unavailable",
  activeHint = "",
  inactiveHint = "",
  ariaLabel = "Toggle availability",
  testid = "availability-slider",
  className = "",
}) {
  const title = active ? activeTitle : inactiveTitle;
  const hint = active ? activeHint : inactiveHint;
  return (
    <div className={`rounded-xl border border-slate-200 bg-white px-3 py-3 ${className}`} data-testid={`${testid}-wrap`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{eyebrow}</div>
          <div className={`text-sm font-semibold ${active ? "text-emerald-700" : "text-slate-700"} truncate`}>
            {title}{hint ? ` — ${hint}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          aria-label={ariaLabel}
          aria-pressed={active}
          data-testid={testid}
          className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60 ${active ? "bg-emerald-600" : "bg-slate-300"}`}
        >
          <span className={`pointer-events-none inline-flex h-6 w-6 items-center justify-center transform rounded-full bg-white shadow ring-0 transition ${active ? "translate-x-7" : "translate-x-0"}`}>
            {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          </span>
        </button>
      </div>
    </div>
  );
}
